import { Table } from "antd";
import { useTranslation } from "react-i18next";
import { Position, SCBOneOperationRun } from "../../../api";
import { PagedApiState } from "../../../api/useApiState";
import "../../../styles/card.scss";
import { SelectedOperationRun } from "../../details/SCBOneDetails";
import { DownloadOperationRun } from "../../DownloadOperationRun";
import { toAntdPagination } from "../../../util";
import { formatLocalizedDateTime } from "../../../util/formatting";

interface SCBOneOperationRunsTableProps {
  operationRuns: PagedApiState<SCBOneOperationRun>;
  positions: Position[];
  positionsLoading: boolean;
  selectedRow: string | undefined;
  onRowSelected: (selectedRun: SelectedOperationRun) => void;
}

export function SCBOneOperationsRunsTable({
  operationRuns,
  positions,
  positionsLoading,
  selectedRow,
  onRowSelected,
}: SCBOneOperationRunsTableProps) {
  const { t } = useTranslation();

  const handleRowSelected = (selectedRun: SelectedOperationRun) => {
    onRowSelected(selectedRun);
  };

  const columns = [
    {
      title: t("start-date"),
      key: "startDateTime",
      render: (r: SCBOneOperationRun) =>
        r.startDatetime ? formatLocalizedDateTime(r.startDatetime) : "-",
    },
    {
      title: t("end-date"),
      key: "endDatetime",
      render: (r: SCBOneOperationRun) =>
        r.endDatetime ? formatLocalizedDateTime(r.endDatetime) : "-",
    },
    {
      key: "DownloadCSV",
      render: (r: SCBOneOperationRun) => (
        <DownloadOperationRun
          from={r.startDatetime}
          to={r.endDatetime}
          runId={r.id}
          positions={positions}
          loading={positionsLoading && selectedRow === r.id}
          disabled={
            positionsLoading || selectedRow !== r.id || positions.length === 0
          }
        />
      ),
    },
  ];

  return (
    <div data-testid="operation-runs-table">
      <Table
        sticky
        dataSource={operationRuns.value?.items || []}
        rowKey={"id"}
        size="middle"
        loading={operationRuns.loading}
        columns={columns}
        onRow={(record, index) => {
          return {
            onClick: () => {
              if (
                typeof index === "number" &&
                operationRuns.value?.items &&
                index >= 0 &&
                index < operationRuns.value.items.length
              ) {
                const selectedRun: SelectedOperationRun = {
                  runId: record.id,
                  serial: operationRuns.value.items[index].device.serial,
                  startDate: new Date(record.startDatetime),
                  endDate: record.endDatetime
                    ? new Date(record.endDatetime)
                    : null,
                };
                handleRowSelected(selectedRun);
              }
            },
          };
        }}
        pagination={toAntdPagination(operationRuns, false)}
        rowClassName={(record) =>
          record.id === selectedRow
            ? "selected-row highlighted-row"
            : "highlighted-row"
        }
      />
    </div>
  );
}
